function slugifyHeading(text: string) {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .slice(0, 60)
}

function parseInline(text: string) {
  const parts: React.ReactNode[] = []
  let lastIndex = 0

  // Markdown links [text](url)
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g
  let match: RegExpExecArray | null

  while ((match = linkRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(parseBold(text.slice(lastIndex, match.index)))
    }
    const [full, label, href] = match
    const isExternal = /^https?:\/\//.test(href)
    parts.push(
      <a
        key={`link-${match.index}`}
        href={href}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
        className="text-primary hover:text-primary-light underline underline-offset-4"
      >
        {parseBold(label)}
      </a>,
    )
    lastIndex = match.index + full.length
  }

  if (lastIndex < text.length) {
    parts.push(parseBold(text.slice(lastIndex)))
  }

  return parts.length > 0 ? parts : text
}

function parseBold(text: string) {
  const parts: React.ReactNode[] = []
  let lastIndex = 0
  const boldRegex = /\*\*([^*]+)\*\*/g
  let match: RegExpExecArray | null

  while ((match = boldRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }
    parts.push(
      <strong key={`bold-${match.index}`} className="font-semibold text-white">
        {match[1]}
      </strong>,
    )
    lastIndex = match.index + match[0].length
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return parts.length > 0 ? parts : text
}

function renderMarkdown(content: string) {
  const lines = content.split('\n')
  const elements: React.ReactNode[] = []
  let listItems: string[] = []
  let key = 0

  const flushList = () => {
    if (listItems.length === 0) return
    elements.push(
      <ul key={`list-${key++}`} className="mt-4 space-y-2 list-disc list-inside text-white/80">
        {listItems.map((item, i) => (
          <li key={i}>{parseInline(item.replace(/^- \s*/, ''))}</li>
        ))}
      </ul>,
    )
    listItems = []
  }

  lines.forEach((line) => {
    const trimmed = line.trim()

    if (trimmed.startsWith('## ')) {
      flushList()
      const title = trimmed.replace(/^##\s*/, '')
      const id = slugifyHeading(title)
      elements.push(
        <h2
          key={`h2-${key++}`}
          id={id}
          className="font-display text-2xl md:text-3xl text-white mt-10 mb-4 scroll-mt-24"
        >
          {parseInline(title)}
        </h2>,
      )
      return
    }

    if (trimmed.startsWith('### ')) {
      flushList()
      elements.push(
        <h3
          key={`h3-${key++}`}
          className="font-display text-xl text-primary mt-6 mb-3"
        >
          {parseInline(trimmed.replace(/^###\s*/, ''))}
        </h3>,
      )
      return
    }

    if (trimmed.startsWith('- ')) {
      listItems.push(trimmed)
      return
    }

    flushList()

    if (trimmed === '') return

    elements.push(
      <p key={`p-${key++}`} className="mt-4 text-white/80 leading-relaxed">
        {parseInline(trimmed)}
      </p>,
    )
  })

  flushList()
  return elements
}

export function extractHeadings(content: string) {
  return content
    .split('\n')
    .filter((line) => line.trim().startsWith('## '))
    .map((line) => {
      const title = line.trim().replace(/^##\s*/, '')
      return { title, id: slugifyHeading(title) }
    })
}

interface NewsContentProps {
  content: string
}

export function NewsContent({ content }: NewsContentProps) {
  return <div className="prose prose-invert max-w-none break-words">{renderMarkdown(content)}</div>
}
